// Authentication service for custom Appwrite backend
class AuthService {
    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL;
        this.user = null;
        this.loading = false;
        this.authChecked = false;
        
        // Try to restore user from sessionStorage if available
        const storedUser = sessionStorage.getItem('auth_user');

        if (!storedUser) {
            return;
        }

        if (storedUser === 'undefined' || storedUser === 'null') {
            sessionStorage.removeItem('auth_user');
            return;
        }

        try {
            this.user = JSON.parse(storedUser);
        } catch {
            // Corrupt cached payload should behave like no cached user.
            sessionStorage.removeItem('auth_user');
        }
    }

    // Check if user is already logged in
    async checkAuth() {
        // If we already checked and failed in this session, don't keep hammering the server
        if (this.authChecked && !this.user) {
            return null;
        }

        this.loading = true;
        try {
            const response = await fetch(`${this.baseURL}/auth/me`, {
                method: 'GET',
                credentials: 'include'
            });
            
            this.authChecked = true;
            
            if (response.ok) {
                const userData = await response.json();
                this.user = userData.user;
                sessionStorage.setItem('auth_user', JSON.stringify(userData.user));
                this.loading = false;
                return userData.user;
            } else if (response.status === 401) {
                // Not authenticated
                this.user = null;
                sessionStorage.removeItem('auth_user');
                this.loading = false;
                return null;
            } else {
                // Server error - don't clear stored user immediately
                if (response.status >= 500 && this.user) {
                    this.loading = false;
                    return this.user;
                }
                
                this.user = null;
                sessionStorage.removeItem('auth_user');
                this.loading = false;
                return null;
            }
        } catch (error) {
            // If we have cached user data and it's just a network issue, keep them logged in
            if (this.user) {
                this.loading = false;
                return this.user;
            }
            
            this.user = null;
            this.loading = false;
            return null;
        }
    }

    // Handle login
    async login(username, password) {
        try {
            const response = await fetch(`${this.baseURL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    username: username,
                    password: password
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                this.user = result.user;
                this.authChecked = true;
                sessionStorage.setItem('auth_user', JSON.stringify(result.user));
                return { success: true, user: result.user };
            } else {
                let errorMessage = 'Login failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorData.message || 'Login failed';
                } catch {
                    if (response.status === 401) {
                        errorMessage = 'Invalid username or password';
                    } else if (response.status >= 500) {
                        errorMessage = 'Server error. Please try again later.';
                    }
                }
                
                return { success: false, error: errorMessage };
            }
        } catch (error) {
            return { success: false, error: 'Network error. Please try again.' };
        }
    }


    // Get current user
    getCurrentUser() {
        return this.user;
    }

    // Check if user is authenticated
    isAuthenticated() {
        return this.user !== null;
    }

    // Check if loading
    isLoading() {
        return this.loading;
    }
}

const authService = new AuthService();
export default authService;
